import { Component, OnInit, AfterViewChecked } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UsuarioService } from '../../../../services/usuario.service';
import { CitySelectService } from '../../../../services/selects/city-select.service';
import { ZoneSelectService } from '../../../../services/selects/zone-select.service';
import {
  CustomReportService,
  FinancialAnalysisPayload,
  FinancialAnalysisRow
} from '../../../../services/customers/custom-report.service';
import { SelectedMonthYear } from '../../../../shared/components/month-year-multi-select/month-year-multi-select.component';
import { createPlainColumn, createCurrencyColumn } from '../../../../shared/components/kite-grid/column-utils/column-utils';

declare const feather: any;

@Component({
  selector: 'app-financial-analysis-report',
  templateUrl: './financial-analysis-report.component.html',
  styleUrls: ['./financial-analysis-report.component.scss'],
  standalone: false
})
export class FinancialAnalysisReportComponent implements OnInit, AfterViewChecked {

  loading   = false;
  exporting = false;
  hasRun    = false;
  gridData: any[] = [];

  columnDefs: any[] = [];

  // ── FormControls matching standard widget API ──────────────────────────────
  salespersonControl = new UntypedFormControl('');
  cityControl        = new UntypedFormControl('');
  zoneControl        = new UntypedFormControl('');
  zipFilter          = '';

  // ── Month selection from widget event ──────────────────────────────────────
  selectedMonthYears: SelectedMonthYear[] = [];

  // ── Dropdown sources ───────────────────────────────────────────────────────
  salespeople: any[] = [];
  cities: any[]      = [];
  zones: any[]       = [];

  // ── Summary totals ─────────────────────────────────────────────────────────
  totalGross  = 0;
  totalClean  = 0;
  totalWaste  = 0;
  totalPayout = 0;

  totalRecords = 0;
  pageSize     = 25;

  constructor(
    private customReportService: CustomReportService,
    private userService: UsuarioService,
    private citySelectService: CitySelectService,
    private zoneSelectService: ZoneSelectService
  ) {}

  ngOnInit(): void {
    this.loadDropdowns();
    this.buildColumnDefs();
  }

  private buildColumnDefs(): void {
    const num = (digits: number) => (p: any) =>
      p.value == null ? '' : Number(p.value).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

    this.columnDefs = [
      createPlainColumn({ field: 'client_id', headerName: 'Client ID', width: 120, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'client_name', headerName: 'Client', width: 250, cellClass: 'ag-cell-left' }),
      createPlainColumn({ field: 'address', headerName: 'Address', width: 300, cellClass: 'ag-cell-left' }),
      createPlainColumn({ field: 'salesperson', headerName: 'Salesperson', width: 150, cellClass: 'ag-cell-left' }),
      createPlainColumn({ field: 'reconciliation_type', headerName: 'Payment Type', width: 130, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'month', headerName: 'Month', width: 100, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'gross_gallons', headerName: 'Collected (gal)', width: 130, cellClass: 'ag-cell-right', valueFormatter: num(2) }),
      createPlainColumn({ field: 'clean_gallons', headerName: 'Clean Oil (gal)', width: 130, cellClass: 'ag-cell-right', valueFormatter: num(2) }),
      createPlainColumn({ field: 'waste_gallons', headerName: 'Waste (gal)', width: 110, cellClass: 'ag-cell-right', valueFormatter: num(2) }),
      createPlainColumn({ field: 'collections_count', headerName: 'Collections', width: 120, cellClass: 'ag-cell-center', valueFormatter: num(0) }),
      createCurrencyColumn({ field: 'cost', headerName: 'Cost ($)', width: 110, cellClass: 'ag-cell-right' }),
      createCurrencyColumn({ field: 'payout', headerName: 'Payout ($)', width: 125, cellClass: 'ag-cell-right' })
    ];
  }

  ngAfterViewChecked(): void {
    if (typeof feather !== 'undefined') {
      feather.replace();
    }
  }

  onMonthSelectionChange(selection: SelectedMonthYear[]): void {
    this.selectedMonthYears = selection;
  }

  private loadDropdowns(): void {
    this.userService.getAllUsers().then((data: any) => {
      this.salespeople = data?.users || [];
    }).catch(() => {});

    this.citySelectService.getAllAsync().then((data: any) => {
      this.cities = data?.citysel || [];
    }).catch(() => {});

    this.zoneSelectService.getAllAsync().then((data: any) => {
      this.zones = data?.zonesel || [];
    }).catch(() => {});
  }

  runReport(): void {
    if (!this.selectedMonthYears.length) return;
    this.loading = true;
    this.hasRun  = false;

    this.customReportService.runFinancialAnalysis(this.buildPayload()).subscribe({
      next: (res) => {
        if (res.ok) {
          this.gridData = res.data;
          this.totalRecords = res.data.length;
          this.computeTotals(res.data);
          this.hasRun = true;
        }
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  exportExcel(): void {
    if (!this.selectedMonthYears.length) return;
    this.exporting = true;

    this.customReportService.exportFinancialAnalysisExcel(this.buildPayload()).subscribe({
      next: (blob: Blob) => {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href  = url;
        link.download = `financial-analysis-${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
        this.exporting = false;
      },
      error: () => { this.exporting = false; }
    });
  }
  private buildPayload(): FinancialAnalysisPayload {
    const months = this.selectedMonthYears.map(m =>
      `${m.year}-${String(m.month + 1).padStart(2, '0')}`
    );
    return {
      months,
      salesperson_id: this.salespersonControl.value || undefined,
      city:           this.cityControl.value        || undefined,
      zone:           this.zoneControl.value        || undefined,
      zip:            this.zipFilter                || undefined
    };
  }

  private computeTotals(rows: FinancialAnalysisRow[]): void {
    this.totalGross  = rows.reduce((s, r) => s + r.gross_gallons, 0);
    this.totalClean  = rows.reduce((s, r) => s + r.clean_gallons, 0);
    this.totalWaste  = rows.reduce((s, r) => s + r.waste_gallons, 0);
    this.totalPayout = rows.reduce((s, r) => s + r.payout, 0);
  }

  clearFilters(): void {
    this.selectedMonthYears = [];
    this.salespersonControl.setValue('');
    this.cityControl.setValue('');
    this.zoneControl.setValue('');
    this.zipFilter = '';
    this.hasRun    = false;
    this.totalRecords = 0;
    this.gridData = [];
  }
}
